import React, { useState, useCallback } from 'react'
import { FormikProps, useFormikContext, FormikHandlers } from 'formik'
import { useStyles } from './PlanFeatures.styles'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import {
  LabraToolTip,
  Button,
  CheckBoxFieldv2,
  TextInput,
} from '@labrav/react-components'
import { InfoOutlined } from '@mui/icons-material'
import clsx from 'clsx'
import {
  FeatureStatus,
  PrivateOffer,
  Features,
} from '../../../../../../modules/privateOffer/reducer'
import {
  planFeaturesDescription,
  planFeatureCustomiseText,
  autoRenewDescription,
} from '../../../../../../common/utils/constants'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { getRandom } from '../../../../../../common/utils/helperFunctions'
import { Product } from '../../../../../../modules/productsListing/reducer'
import { get, isEmpty } from 'lodash'
import { useCommonStyles } from '../../../../../../common/styles/common.styles'

export type PrivateOfferAddEditProps = {
  product: Product
  planId: string
}
export const PlanFeatures: React.FC<PrivateOfferAddEditProps> = ({
  product,
  planId,
}) => {
  const { classes: commonClasses } = useCommonStyles()
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<PrivateOffer>
  const planFeatures = isEmpty(values.labraPlanFeatures)
    ? product?.plans?.find(plan => plan.planId === planId)?.features
    : values?.labraPlanFeatures
  const [randomKey, setRandomKey] = useState<number>(getRandom())
  const { classes } = useStyles()
  const [updateFeatures, setUpdateFeatures] = useState<boolean>(false)
  const [featuresList, setFeaturesList] = useState<Features[]>([])

  const handleUpdateFeatures = () => {
    setUpdateFeatures(true)
    try {
      setFeaturesList(JSON.parse(JSON.stringify(planFeatures)))
    } catch (e) {
      console.error('unable to clone the features object', e)
    }
  }
  const handleCheckboxChange = useCallback(
    (handleChange: FormikHandlers['handleChange']) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        handleChange({
          target: {
            name: 'autoRenew',
            value: e.target.checked,
          },
        })
      },
    []
  )
  const handleInputChangeSelect = (event: unknown, index: number) => {
    const { value } = (event as React.ChangeEvent<HTMLInputElement>).target

    const labraPlanFeaturesEdit = [...featuresList]
    labraPlanFeaturesEdit[index].status = value
    switch (labraPlanFeaturesEdit[index].status) {
      case FeatureStatus.INCLUDED: {
        labraPlanFeaturesEdit[index].value = 'Yes'
        break
      }
      case FeatureStatus.NOT_INCLUDED: {
        labraPlanFeaturesEdit[index].value = 'No'
        break
      }
      case FeatureStatus.CUSTOM_VALUE:
      case FeatureStatus.NOT_APPLICABLE: {
        labraPlanFeaturesEdit[index].value = ''
        break
      }
      default: {
        break
      }
    }
    setFeaturesList(labraPlanFeaturesEdit)
  }
  const handleInputFieldChange = (event: unknown, index: number) => {
    const { value } = (event as React.ChangeEvent<HTMLInputElement>).target

    const labraPlanFeaturesEdit = [...featuresList]
    labraPlanFeaturesEdit[index].value = value
    setFeaturesList(labraPlanFeaturesEdit)
  }
  const featureTypeOptions = [
    { label: FeatureStatus.CUSTOM_VALUE, value: FeatureStatus.CUSTOM_VALUE },
    { label: FeatureStatus.INCLUDED, value: FeatureStatus.INCLUDED },
    {
      label: FeatureStatus.NOT_APPLICABLE,
      value: FeatureStatus.NOT_APPLICABLE,
    },
    { label: FeatureStatus.NOT_INCLUDED, value: FeatureStatus.NOT_INCLUDED },
  ]
  const handleCancelFeature = () => {
    setUpdateFeatures(false)
  }
  const handleSaveFeature = () => {
    setFieldValue('labraPlanFeatures', featuresList)
    setUpdateFeatures(false)
  }
  const getFeatureValue = (feature: Features) => {
    switch (feature.status) {
      case FeatureStatus.INCLUDED:
        return 'Yes'
      case FeatureStatus.NOT_INCLUDED:
        return 'No'
      default:
        return feature.value
    }
  }
  return (
    <>
      {values?.plan?.planType !== ('USAGE' as string) &&
        values?.plan?.paymentType !== ('CUSTOM' as string) && (
          <div className={classes.autoRenewContainer}>
            <Typography
              className={classes.subHeading}
              data-testid="configure-autorenew-subheading"
            >
              Configure auto-renew
            </Typography>
            <Typography
              className={classes.subheadingInfoText}
              data-testid="configure-autorenew-description"
            >
              {autoRenewDescription}
            </Typography>
            <div className={classes.checkboxContainer} data-testid="checkbox">
              <CheckBoxFieldv2
                keyName="autoRenew"
                title="Auto-renew"
                value={values.autoRenew}
                onChangeBox={handleCheckboxChange(handleChange)}
                onBlur={handleBlur}
              />
            </div>
            {values.autoRenew ? (
              <div
                className={classes.renewalContainer}
                key={`renewalCount-${randomKey}`}
              >
                <TextInput
                  isRequired={false}
                  keyName="renewalCount"
                  title="Number of renewals"
                  updatedValue={values?.renewalCount}
                  value={values?.renewalCount || ''}
                  onChange={handleChange}
                  type="number"
                  onBlur={handleBlur}
                  isError={
                    !!(
                      get(touched, 'renewalCount', false) &&
                      get(errors, 'renewalCount', false)
                    )
                  }
                  errorText={
                    (!!get(touched, 'renewalCount', false) &&
                      get(errors, 'renewalCount', ' ')) ||
                    ' '
                  }
                  isReadOnly={false}
                  placeHolder=""
                  variant="regularV2"
                  keyToPrevent={['+', '-', 'e', '.']}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
        )}
      <div
        className={clsx(
          classes.subHeadingContainer,
          classes.companyDetailSubHead
        )}
      >
        <Typography
          className={classes.subHeading}
          data-testid="features-subheading"
        >
          Features
        </Typography>
        <LabraToolTip
          tooltipPlacement={'right'}
          label={'Update the list of features you want to offer to the buyer.'}
        >
          <InfoOutlined className={classes.infoIcon} />
        </LabraToolTip>
      </div>
      <div
        className={classes.descriptionContainer}
        data-testid="description-info"
      >
        <Typography className={classes.subheadingInfoText}>
          {planFeaturesDescription}
        </Typography>
        <Typography className={classes.subheadingInfoText}>
          {planFeatureCustomiseText}
        </Typography>
      </div>
      {!updateFeatures ? (
        <>
          <div className={classes.featureContainer} data-testid="feature-items">
            {planFeatures?.map(
              (feature, index) =>
                feature.status !== FeatureStatus.NOT_APPLICABLE && (
                  <>
                    <div
                      className={classes.featureTextContainer}
                      data-testid="feature-item"
                      key={JSON.stringify(feature)}
                    >
                      <Typography className={classes.featureText}>
                        {feature.label}
                      </Typography>
                      <Typography className={classes.featureText}>
                        {getFeatureValue(feature)}
                      </Typography>
                    </div>
                    {index < (planFeatures?.length ?? 0) - 1 && (
                      <div className={classes.borderLine} />
                    )}
                  </>
                )
            )}
          </div>
          <Button
            styleType="secondary"
            onClick={handleUpdateFeatures}
            className={classes.updateFeaturesBtn}
            data-testid="update-features-button"
          >
            {'Update features'}
          </Button>
        </>
      ) : (
        <>
          <div className={classes.featureContainer} data-testid="feature-items">
            {featuresList?.map((feature, index) => (
              <>
                <div
                  className={classes.featureItemContainer}
                  data-testid="feature-item"
                >
                  <Typography
                    className={clsx(classes.featureText, classes.fieldWidth)}
                  >
                    {feature.label}
                  </Typography>
                  <div
                    className={classes.planSectionRowContainer}
                    data-testid={'status-type'}
                  >
                    <FormControl className={classes.fieldWidth}>
                      <InputLabel
                        id="feature-status-label"
                        className={classes.inputLabel}
                        classes={{ shrink: commonClasses.shrink }}
                      >
                        Status
                      </InputLabel>
                      <Select
                        labelId="feature-status"
                        value={feature.status}
                        onChange={e => handleInputChangeSelect(e, index)}
                        label="Select status"
                        inputProps={{ 'data-testid': 'inputSelectField' }}
                        variant="standard"
                        className={classes.selectField}
                        IconComponent={props => (
                          <KeyboardArrowDownIcon
                            {...props}
                            fontSize="small"
                            data-testid="arrow-button"
                          />
                        )}
                        classes={{ icon: classes.icon }}
                        name={`labraPlanFeatures.${index}.status`}
                        data-testid={`labraPlanFeatures.${index}.status`}
                      >
                        {(featureTypeOptions || []).map(mode => (
                          <MenuItem
                            data-testid={'menu-item-status-type'}
                            value={mode.value}
                            className={classes.menuRoot}
                            key={mode.value}
                            disabled={false}
                          >
                            {mode.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  {feature.status === FeatureStatus.CUSTOM_VALUE ? (
                    <div
                      className={classes.fieldWidth}
                      key={`featureValue-${randomKey}`}
                      data-testid={'details-input-field'}
                    >
                      <TextInput
                        isRequired={true}
                        keyName={`details-input-field-${index}`}
                        title="Details"
                        value={feature.value}
                        onChange={e => handleInputFieldChange(e, index)}
                        type="text"
                        onBlur={handleBlur}
                        isReadOnly={false}
                        placeHolder=""
                        variant="regularV2"
                        keyToPrevent={['+', '-', 'e', '.']}
                        errorText={''}
                        isError={false}
                      />
                    </div>
                  ) : (
                    <div className={classes.fieldWidth} />
                  )}
                </div>
                {index < (featuresList?.length ?? 0) - 1 && (
                  <div className={classes.borderLineOnUpdate} />
                )}
              </>
            ))}
          </div>
          <div className={classes.saveCancelButton}>
            <Button
              styleType="secondary"
              onClick={handleSaveFeature}
              className={classes.updateFeaturesBtn}
              data-testid="save-features-button"
            >
              {'Save'}
            </Button>
            <Button
              styleType="cancel"
              onClick={handleCancelFeature}
              className={classes.updateFeaturesBtn}
              data-testid="cancel-features-button"
            >
              {'Cancel'}
            </Button>
          </div>
        </>
      )}
    </>
  )
}
